"use client";
import { useState, useRef, useEffect } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";

import { useSelector, useDispatch } from "react-redux";

import classNames from "classnames";
import ReCAPTCHA from "react-google-recaptcha";

import { handleSignupModalOpened, setMailData, setPasswordData } from "@/src/store/signup";

import { isMailAddressUsed, isPhoneNumberUsed, getContract, signup } from "services/auth";

import validate from "helpers/form-validation";
import { onlyNumber } from "helpers/only-number";
import { checkOnlyLetters } from "helpers/only-text";
import { Ga4WithEventParameters } from "helpers/google-tag-manager/events/ga4-with-eventparameters";
import { gtmLoadInterval } from "helpers/google-tag-manager/gtm-load-interval";

import { equalsContractNameToId } from "mock/phone-confirmation";

import { renderContent } from "./modal-contents";
import ModalTextContent from "@/src/components/modal-text-content";
import Input from "components/common/input";
import Button from "components/common/button";
import CheckBoxItem from "components/common/checkbox-item";
import ModalSignupEmailResult from "components/modal-signup-email-result";

import styles from "./styles.module.css";

const DynamicPhoneConfirmationModal = dynamic(() => import("components/common/phone-confirmation"), { ssr: false });
const Modal = dynamic(() => import("components/common/modal"), { ssr: false });

const IndividualSignupForms = () => {
  const [form, setForm] = useState({
    memberFullName: "",
    mobilePhone: null,
    emailAddress: null,
    password: null,
    phoneConfirmationApproved: false,
    allowCommunication: false,
    allowSms: false,
    allowInfoShare: false,
    membershipContractApproved: true,
    formType: "individual_registration@modal",
    contracts: null,
    application: "desktop-web",
    allowNotification: true,
    gRecaptchaResponse: null,
  });
  const [errors, setErrors] = useState({
    memberFullName: {
      value: null,
      message: null,
    },
    mobilePhone: {
      value: null,
      message: null,
    },
    emailAddress: {
      value: null,
      message: null,
    },
    password: {
      value: null,
      message: null,
    },
    wrongUser: {
      value: null,
      message: null,
    },
    phoneConfirmationApproved: {
      value: null,
      message: null,
    },
    individualKvkk: {},
    membership: {},
    explicitConsent: {},
  });

  const [isVisibleUserPassword, setIsVisibleUserPassword] = useState(false);
  const [isFormSending, setIsFormSending] = useState(false);
  const [isOpenAgreements, setIsOpenAgreements] = useState(false);
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [selectedModalContent, setSelectedModalContent] = useState(null);
  const [isOpenedPhoneConfirmationModal, setIsOpenedPhoneConfirmationModal] = useState(false);
  const [confirmationStep, setConfirmationStep] = useState("VERIFICATION"); // verification, change-phone-number, success-confirmation

  const router = useRouter();
  const dispatch = useDispatch();
  const recaptchaRef = useRef();

  const signupModalReduxState = useSelector((state) => state.signup.signupModalOpened);

  const individualKvkkCheckboxTitleClass = classNames({
    [styles.checkContent]: true,
    [styles.noncheckContent]: errors.individualKvkk.value === true,
  });

  const membershipCheckboxTitleClass = classNames({
    [styles.checkContent]: true,
    [styles.noncheckContent]: errors.membership.value === true,
  });

  const errorMemberDisplayClass = classNames({
    [styles.errorCheckboxClass]: errors.membership.value === true,
  });

  const errorKvkkDisplayClass = classNames({
    [styles.errorCheckboxClass]: errors.individualKvkk.value === true,
  });

  const handlePasswordInputChange = (value) => {
    if (value.length > 0) {
      setIsVisibleUserPassword(true);
    } else {
      setIsVisibleUserPassword(false);
    }

    setForm({
      ...form,
      password: value,
    });
  };

  const handleInputOnChange = (value, type) => {
    setForm({
      ...form,
      [type]: value,
    });

    setIsOpenAgreements(true);
  };

  const handlePhoneInputChange = (value, type) => {
    // If the user entered a number and then gave up and deleted all numbers, it should be assigned null.
    // Because BE only accepts null values.
    if (value.length === 0) {
      setForm({
        ...form,
        mobilePhone: null,
      });
      return;
    }

    const isValidPhoneNumber = onlyNumber(value) || value === "" || value.length < 11;
    const isPhoneNumberStartWithTheNumberIWant = value.charAt(0) !== "5";
    if (isValidPhoneNumber) {
      // Mobile phones should start with 5
      if (isPhoneNumberStartWithTheNumberIWant) {
        setTimeout(() => {
          setForm({
            ...form,
            mobilePhone: "",
          });
        }, 100);
        return;
      }
      setForm({
        ...form,
        mobilePhone: value,
      });
      setIsOpenAgreements(true);
    }
  };

  const handleNameInputChange = (value, type) => {
    if (checkOnlyLetters(value)) {
      setForm({
        ...form,
        [type]: value,
      });
      setIsOpenAgreements(true);
    }
  };

  const handleInputFocus = (e, type) => {
    setErrors({ ...errors, [type]: { value: false, message: null }, wrongUser: { value: false, message: null } });
  };

  const handleRegisterButton = async (e) => {
    e.preventDefault();
    setIsFormSending(true);
    const isValid = await formValidate();
    if (!isValid) {
      setIsFormSending(false);
      return null;
    }

    if (form.mobilePhone && form.mobilePhone.length === 10) {
      setIsOpenedPhoneConfirmationModal(true);
      recaptchaRef.current.reset();
      setIsFormSending(false);
    } else {
      sendForm();
    }
  };

  const sendForm = async () => {
    const gRecaptchaResponse = await recaptchaRef.current.executeAsync();
    const sendData = {
      ...form,
      gRecaptchaResponse,
    };

    const postSignupService = await signup(sendData, "personal");

    if (postSignupService.status === "success") {
      Ga4WithEventParameters({
        eventName: "register_individual",
        eventParameters: { method: "email", user_id: postSignupService.result?.memberId },
      });
      dispatch(setMailData(form.emailAddress));
      dispatch(setPasswordData(form.password));
      dispatch(handleSignupModalOpened());
      setIsFormSending(false);
    }
    recaptchaRef.current.reset();
    setIsFormSending(false);
  };

  const formValidate = async () => {
    const fieldErrors = await validate({
      name: form.memberFullName === "" ? null : form.memberFullName,
      email: form.emailAddress === "" ? null : form.emailAddress,
      password: form.password === "" ? null : form.password,
    });
    const errorName = fieldErrors.name;
    let errorEmail = fieldErrors.email;
    let errorMobilePhone = "";
    const errorPass = fieldErrors.password;
    const errorPhoneConfirmation = fieldErrors.phoneConfirmation;
    const errorIndividualKvkk = !form.contracts?.find((contract) => contract.templateId === 4).approved;
    const errorMembership = !form.contracts?.find((contract) => contract.templateId === 2).approved;

    if (!errorEmail) {
      const params = {
        emailAddress: form.emailAddress,
      };
      await isMailAddressUsed(params).then((response) => {
        if (!response.result.valid) {
          errorEmail = "*Bu eposta adresi daha önce kullanılmış";
        }
      });
    }

    if (form.mobilePhone && form.mobilePhone.length > 0) {
      if (form.mobilePhone.length !== 10) {
        errorMobilePhone = "Geçerli bir telefon numarası girilmelidir.";
      } else {
        const params = {
          phoneNumber: form.mobilePhone,
          accountType: "PERSONAL",
        };
        await isPhoneNumberUsed(params).then((res) => {
          if (!res.result?.valid) {
            errorMobilePhone = "*Bu telefon numarası daha önce kullanılmış";
          }
        });
      }
    }

    if (
      errorName ||
      errorEmail ||
      errorMobilePhone ||
      errorPass ||
      errorPhoneConfirmation ||
      errorIndividualKvkk ||
      errorMembership
    ) {
      setErrors({
        memberFullName: {
          value: !!errorName,
          message: errorName,
        },
        emailAddress: {
          value: !!errorEmail,
          message: errorEmail,
        },
        mobilePhone: {
          value: !!errorMobilePhone,
          message: errorMobilePhone,
        },
        password: {
          value: !!errorPass,
          message: errorPass,
        },
        individualKvkk: {
          value: errorIndividualKvkk,
          message: errorIndividualKvkk,
        },
        membership: {
          value: errorMembership,
          message: errorMembership,
        },
        phoneConfirmationApproved: {
          value: errorPhoneConfirmation,
          message: "*Doğrulama yapılmalıdır",
        },
      });
      return false;
    }

    return true;
  };

  const handleChangeContractCheckbox =
    ({ key }) =>
    (event) => {
      const {
        target: { checked },
      } = event;

      const contracts = form.contracts.reduce(
        (acc, contract) => {
          if (contract.templateId === equalsContractNameToId[key]) {
            const selectedCheckBoxIndex = acc.indexOf(contract);
            acc[selectedCheckBoxIndex].approved = checked;
          }
          return acc;
        },
        [...form.contracts]
      );

      setForm({ ...form, contracts });
      setErrors({ ...errors, [key]: { value: !checked, message: null } });
    };

  const handleSubscribeCheckboxToggle = (key) => (event) => {
    const {
      target: { checked },
    } = event;

    setForm({ ...form, [key]: checked });
    setForm({ ...form, allowSms: checked });
    setErrors({ ...errors, [key]: { value: false, message: null } });
  };

  const handleModalClose = () => setIsContractModalOpen(false);
  const handleModalOpen = (value) => {
    setSelectedModalContent(value);
    setIsContractModalOpen(true);
  };

  const getContractContent = async () => {
    try {
      const response = await getContract(form.formType);
      if (response.status !== "success") return;

      const { result } = response;
      let contracts = [];
      const hasResult = result?.length > 0;

      if (hasResult) {
        contracts = result.reduce((acc, contractItem) => {
          const { templateId, version } = contractItem;
          acc.push({
            templateId,
            version,
            approved: false,
          });
          return acc;
        }, []);
      }
      setForm({ ...form, contracts });
    } catch (error) {
      console.error({ error });
    }
  };

  const handleClosePhoneConfirmationModal = () => {
    recaptchaRef.current.reset();
    setIsOpenedPhoneConfirmationModal(false);
  };

  useEffect(() => {
    getContractContent();
    gtmLoadInterval(() => {
      Ga4WithEventParameters({ eventName: "form", eventParameters: { label: "view||register-individual-form" } });
    });
  }, []);

  useEffect(() => {
    if (!isOpenedPhoneConfirmationModal && confirmationStep === "SUCCESS_CONFIRMATON") router.push("/giris");
  }, [isOpenedPhoneConfirmationModal]);

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.formWrapper}>
          <form>
            <div>
              <label htmlFor="name" className={styles.inputTitle}>
                Ad - Soyad
              </label>
              <div className={styles.mailInput}>
                <Input
                  errorMessage={errors?.memberFullName?.message}
                  inputClass={styles.loginInput}
                  type="text"
                  onChange={(e) => handleNameInputChange(e.target.value, "memberFullName")}
                  onFocus={(e) => handleInputFocus(e.target.value, "memberFullName")}
                  placeholder="Ad - Soyad Giriniz"
                  value={form.memberFullName || ""}
                />
              </div>
            </div>
            <div className={styles.fieldMargin}>
              <label htmlFor="phone" className={styles.inputTitle}>
                Cep Telefonu <span className={styles.faintLabel}>(Opsiyonel)</span>
              </label>
              <div className={styles.mailInput}>
                <Input
                  errorMessage={errors?.mobilePhone?.message}
                  inputClass={styles.loginInput}
                  type="text"
                  onChange={(e) => handlePhoneInputChange(e.target.value, "mobilePhone")}
                  onFocus={(e) => handleInputFocus(e.target.value, "mobilePhone")}
                  placeholder="(5XX) XXX XX XX"
                  value={form.mobilePhone || ""}
                  phone
                />
              </div>
            </div>
            <div className={styles.fieldMargin}>
              <label htmlFor="email" className={styles.inputTitle}>
                E-posta Adresi
              </label>
              <div className={styles.mailInput}>
                <Input
                  errorMessage={errors?.emailAddress?.message}
                  inputClass={styles.loginInput}
                  type="text"
                  onChange={(e) => handleInputOnChange(e.target.value, "emailAddress")}
                  onFocus={(e) => handleInputFocus(e.target.value, "emailAddress")}
                  placeholder="E-posta Adresinizi Giriniz"
                  value={form.emailAddress || ""}
                />
              </div>
            </div>
            <div className={styles.fieldMargin}>
              <label htmlFor="password" className={styles.inputTitle}>
                Şifre
              </label>
              <div className={styles.mailInput}>
                <Input
                  errorMessage={errors?.password?.message}
                  password
                  showHideButton={isVisibleUserPassword}
                  inputClass={styles.loginInput}
                  onChange={(e) => handlePasswordInputChange(e.target.value)}
                  onFocus={(e) => handleInputFocus(e.target.value, "password")}
                  placeholder="Şifrenizi Giriniz"
                  value={form.password || ""}
                />
              </div>
            </div>
            {isOpenAgreements && (
              <div className={styles.actionWrap}>
                <div className={styles.formGroup}>
                  <CheckBoxItem
                    title={
                      <div className={membershipCheckboxTitleClass}>
                        <span onClick={() => handleModalOpen("SIGNUP_CONTRACT")}>Üyelik Sözleşmesini</span> ve{" "}
                        <span onClick={() => handleModalOpen("SIGNUP_CONTRACT")}>gizlilik politikasını</span> okudum,
                        onaylıyorum
                      </div>
                    }
                    name="membership"
                    onChange={handleChangeContractCheckbox({ key: "membership" })}
                    displayClass={errorMemberDisplayClass}
                  />
                </div>
                <div className={styles.formGroup}>
                  <CheckBoxItem
                    title={
                      <div className={individualKvkkCheckboxTitleClass}>
                        Kişisel verilerin işlenmesiyle ilgili{" "}
                        <span onClick={() => handleModalOpen("KVKK")}>Aydınlatma Metni</span>
                        {`'ni okuyup anladığımı beyan ve kabul ederim.`}
                      </div>
                    }
                    name="individualKvkk"
                    onChange={handleChangeContractCheckbox({ key: "individualKvkk" })}
                    displayClass={errorKvkkDisplayClass}
                  />
                </div>
                <div className={styles.formGroup}>
                  <CheckBoxItem
                    title={
                      <div className={styles.checkContent}>
                        Verilerimin <span onClick={() => handleModalOpen("EXPLICIT_CONSENT")}>Açık Rıza Metni</span>
                        {`'nde belirtilen şekilde işlenmesine onay veriyorum.`}
                      </div>
                    }
                    name="explicitConsent"
                    onChange={handleChangeContractCheckbox({ key: "explicitConsent" })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <CheckBoxItem
                    title={
                      <div className={styles.checkContent}>
                        Tüm duyuru ve kampanyalardan ticari e-ileti yoluyla haberdar olmak istiyorum.
                      </div>
                    }
                    name="subscribe"
                    onChange={handleSubscribeCheckboxToggle("allowCommunication")}
                  />
                </div>
                <div className={styles.formGroup}>
                  <ReCAPTCHA
                    key="captcha"
                    ref={recaptchaRef}
                    sitekey={window.env.EJ_KEYS_RECAPTCHA}
                    size="invisible"
                    badge="inline"
                  />
                </div>
                <Modal printButton isOpen={isContractModalOpen} handleClose={() => handleModalClose()}>
                  <ModalTextContent>{renderContent(selectedModalContent)}</ModalTextContent>
                </Modal>
              </div>
            )}
            <div className={styles.loginButton}>
              <Button size="M" type="submit" onClick={(e) => handleRegisterButton(e)} isLoading={isFormSending}>
                Üye Ol
              </Button>
            </div>
            <div className={styles.signUp}>
              <p>Zaten üye misin?</p>
              <span>
                <a href="/giris/" className={styles.forgetPasswordLink}>
                  Giriş Yap
                </a>
              </span>
            </div>
            <Modal isOpen={isOpenedPhoneConfirmationModal} handleClose={handleClosePhoneConfirmationModal}>
              <DynamicPhoneConfirmationModal
                form={form}
                setForm={setForm}
                confirmationStep={confirmationStep}
                setConfirmationStep={setConfirmationStep}
                recaptchaRef={recaptchaRef}
              />
            </Modal>
          </form>
          <Modal isOpen={signupModalReduxState} handleClose={() => dispatch(handleSignupModalOpened())}>
            <ModalSignupEmailResult />
          </Modal>
        </div>
      </div>
    </>
  );
};

IndividualSignupForms.propTypes = {};

export default IndividualSignupForms;
